import React, { useRef, useState } from 'react';
import {
    Alert,
    Button,
    Code,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import { CheckCircle, FileCsv, Warning } from '@phosphor-icons/react';

import { readApiError } from '@utils/apiError';

/**
 * Shape every bulk-create endpoint returns. Products and materials differ only in
 * the per-row error key (`sku` vs `code`), which the table reads generically.
 */
export type BulkCreateResult = {
    totalRows: number;
    created: number;
    skipped: number;
    /** Only sent by upsert importers — rows that already existed and were changed. */
    updated?: number;
    errors: Array<{ row: number; reason: string; sku?: string; code?: string }>;
    dryRun: boolean;
};

interface ImportCsvModalProps {
    opened: boolean;
    onClose: () => void;
    /** Fired after a committed import so the grid can reload. */
    onImported: () => void;
    /** Singular noun used throughout the copy, e.g. "product". */
    entityNoun: string;
    /** Every column the importer understands, in template order. */
    columns: readonly string[];
    /** Which of `columns` must be present. Named explicitly in the hint. */
    requiredColumns: readonly string[];
    /** Natural-key column name, e.g. "SKU" — shown in the error table header. */
    keyLabel: string;
    /** Uploads the file; `dryRun` validates and counts without writing. */
    upload: (file: File, dryRun: boolean) => Promise<BulkCreateResult>;
    /**
     * 'create' skips rows that already exist; 'upsert' updates them.
     *
     * Only the wording changes here — the importer decides the behaviour. But the
     * difference matters enough to say out loud: somebody uploading prices over
     * existing rows needs to know they will be overwritten, and somebody
     * uploading new products needs to know they will not.
     */
    mode?: 'create' | 'upsert';
}

type Step = 'choose' | 'preview' | 'done';

/** "a, b and c" — so the hint reads as a sentence rather than a CSV of its own. */
const formatColumnList = (names: readonly string[]): string => {
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** Errors are per-row and a bad file can produce hundreds; show a readable slice. */
const MAX_ERRORS_SHOWN = 10;

const ImportCsvModal: React.FC<ImportCsvModalProps> = ({
    opened,
    onClose,
    onImported,
    entityNoun,
    columns,
    requiredColumns,
    keyLabel,
    upload,
    mode = 'create',
}) => {
    const isUpsert = mode === 'upsert';
    /** Rows a run will touch — the enabled/disabled state of the commit button. */
    const affected = (r: BulkCreateResult) => r.created + (r.updated ?? 0);
    const [step, setStep] = useState<Step>('choose');
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<BulkCreateResult | null>(null);
    const [outcome, setOutcome] = useState<BulkCreateResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setStep('choose');
        setFile(null);
        setPreview(null);
        setOutcome(null);
        setBusy(false);
        setError(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Selecting a file runs the dry run immediately — nothing is written yet.
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = e.target.files?.[0];
        if (!chosen) return;
        if (fileInputRef.current) fileInputRef.current.value = '';

        setBusy(true);
        setError(null);
        try {
            const result = await upload(chosen, true);
            setFile(chosen);
            setPreview(result);
            setStep('preview');
        } catch (err) {
            setError(readApiError(err, 'Could not read that file.'));
        } finally {
            setBusy(false);
        }
    };

    const handleCommit = async () => {
        if (!file) return;
        setBusy(true);
        setError(null);
        try {
            const result = await upload(file, false);
            setOutcome(result);
            setStep('done');
            onImported();
        } catch (err) {
            setError(readApiError(err, `Could not import the ${entityNoun}s.`));
        } finally {
            setBusy(false);
        }
    };

    const renderErrorTable = (result: BulkCreateResult) => (
        <ScrollArea.Autosize mah={200}>
            <Table striped withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Row</Table.Th>
                        <Table.Th>{keyLabel}</Table.Th>
                        <Table.Th>Problem</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {result.errors.slice(0, MAX_ERRORS_SHOWN).map((e) => (
                        <Table.Tr key={`${e.row}-${e.sku ?? e.code ?? ''}`}>
                            <Table.Td>{e.row}</Table.Td>
                            <Table.Td>{e.sku ?? e.code ?? '—'}</Table.Td>
                            <Table.Td>{e.reason}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
            {result.errors.length > MAX_ERRORS_SHOWN && (
                <Text size="xs" c="dimmed" mt="xs">
                    …and {result.errors.length - MAX_ERRORS_SHOWN} more
                </Text>
            )}
        </ScrollArea.Autosize>
    );

    const optionalColumns = columns.filter((c) => !requiredColumns.includes(c));

    const renderChoose = () => (
        <Stack gap="md">
            <Text size="sm" c="dimmed">
                Upload a CSV with a header row.{' '}
                <Text span fw={600} c="dark">
                    {formatColumnList(requiredColumns)}
                </Text>{' '}
                {requiredColumns.length === 1 ? 'is' : 'are'} required.
                {optionalColumns.length > 0 && (
                    <>
                        {' '}
                        {formatColumnList(optionalColumns)}{' '}
                        {optionalColumns.length === 1 ? 'is' : 'are'} optional.
                    </>
                )}
            </Text>

            <Code block>{columns.join(',')}</Code>

            <Text size="xs" c="dimmed">
                {isUpsert
                    ? `Rows that already exist are updated with the values you supply. Leave a column blank to keep what is already there.`
                    : `The ${keyLabel.toLowerCase()} is also used as the ${entityNoun} ID. Rows whose ${keyLabel.toLowerCase()} already exists are skipped, never overwritten.`}
            </Text>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {error && (
                <Alert color="red" icon={<Warning size={16} />}>
                    {error}
                </Alert>
            )}

            <Group justify="flex-end">
                <Button variant="subtle" color="gray" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    leftSection={<FileCsv size={16} />}
                    loading={busy}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Choose CSV file
                </Button>
            </Group>
        </Stack>
    );

    const renderPreview = () => {
        if (!preview) return null;
        const nothingToDo = affected(preview) === 0;

        return (
            <Stack gap="md">
                <Alert
                    color={preview.errors.length > 0 ? 'yellow' : 'blue'}
                    icon={<FileCsv size={16} />}
                    title={file?.name}
                >
                    <Text size="sm">
                        <strong>{preview.created}</strong> {entityNoun}
                        {preview.created === 1 ? '' : 's'} will be created
                        {isUpsert && ` · ${preview.updated ?? 0} updated`}
                        {preview.skipped > 0 &&
                            (isUpsert
                                ? ` · ${preview.skipped} unchanged`
                                : ` · ${preview.skipped} skipped (SKU already exists)`)}
                        {preview.errors.length > 0 &&
                            ` · ${preview.errors.length} row${preview.errors.length === 1 ? '' : 's'} with problems`}
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                        Nothing has been saved yet.
                    </Text>
                </Alert>

                {preview.errors.length > 0 && renderErrorTable(preview)}

                {error && (
                    <Alert color="red" icon={<Warning size={16} />}>
                        {error}
                    </Alert>
                )}

                <Group justify="space-between">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={() => setStep('choose')}
                    >
                        Back
                    </Button>
                    <Button
                        onClick={handleCommit}
                        loading={busy}
                        disabled={nothingToDo}
                    >
                        {nothingToDo
                            ? 'Nothing to import'
                            : isUpsert
                              ? `Apply to ${affected(preview)} row${affected(preview) === 1 ? '' : 's'}`
                              : `Create ${preview.created} ${entityNoun}${preview.created === 1 ? '' : 's'}`}
                    </Button>
                </Group>
            </Stack>
        );
    };

    const renderDone = () => {
        if (!outcome) return null;

        return (
            <Stack gap="md">
                <Alert
                    color="green"
                    icon={<CheckCircle size={16} />}
                    title={
                        isUpsert
                            ? `${outcome.created} added, ${outcome.updated ?? 0} updated`
                            : `${outcome.created} ${entityNoun}${outcome.created === 1 ? '' : 's'} added`
                    }
                >
                    <Text size="xs">
                        {isUpsert ? (
                            outcome.skipped > 0 ? (
                                `${outcome.skipped} row${outcome.skipped === 1 ? '' : 's'} already matched what you uploaded.`
                            ) : null
                        ) : (
                            <>
                                {outcome.skipped > 0 &&
                                    `${outcome.skipped} skipped because the ${keyLabel.toLowerCase()} already existed. `}
                                They won&apos;t show stock or channel inventory until
                                they&apos;re mapped to a channel and a feed reports them.
                            </>
                        )}
                    </Text>
                </Alert>

                {outcome.errors.length > 0 && renderErrorTable(outcome)}

                <Group justify="flex-end">
                    <Button onClick={handleClose}>Done</Button>
                </Group>
            </Stack>
        );
    };

    const stepTitle: Record<Step, string> = {
        choose: `Import ${entityNoun}s from CSV`,
        preview: 'Review before importing',
        done: 'Import complete',
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={stepTitle[step]}
            size="lg"
            centered
        >
            {step === 'choose' && renderChoose()}
            {step === 'preview' && renderPreview()}
            {step === 'done' && renderDone()}
        </Modal>
    );
};

export default ImportCsvModal;
